import { NormalizedVariant, ProductVariant, Option } from './types'
import sortBy from 'lodash/sortBy'
import isEmpty from 'lodash/isEmpty'
import { normalizeSourceId } from 'components/ProductsAndModifiers/utils'

/*
 *  This replaces lodash snakeCase due to some names has trailing white space
 *
 */
const toSnakeCasePreserveTrailingSpace = (str: string) => {
  const match = str.match(/(\s*)$/) // Capture trailing whitespace
  const trailingSpaces = match ? match[0] : ''
  const main = str.slice(0, -trailingSpaces.length || undefined) // Main part without trailing spaces
  const snake = main.replace(/\s+/g, '_') // Replace internal spaces
  return snake.toLocaleLowerCase() + trailingSpaces
}

/*
 *  this filters an array of variants with the name of an option
 *  with options (ala variant group names) as Size, Color etc. etc.
 *
 */
const getVariantsByOption = (
  variants: ProductVariant[],
  optionName: string,
  optionValues: string | string[]
) => {
  return (variants || []).filter((variant) => {
    return !!(variant.options || []).filter(
      (option) =>
        toSnakeCasePreserveTrailingSpace(option.name) ===
          toSnakeCasePreserveTrailingSpace(optionName) &&
        optionValues.includes(option.value)
    ).length
  })
}

/*
 * this groups variants by variant group name
 * with variant group name as Size, Color etc.
 * */
const GroupVariantsByGroupName = (
  options: Option[],
  variants: ProductVariant[]
) => {
  return options.reduce(
    (map: Record<string, ProductVariant[]>, { name, values }) => {
      map[toSnakeCasePreserveTrailingSpace(name)] = getVariantsByOption(
        variants,
        name,
        values
      )
      return map
    },
    {}
  )
}

const GroupVariantsByOptionName = (
  options: string[],
  variantGroupName: string,
  variants: ProductVariant[]
) => {
  return options.reduce((map: Record<string, ProductVariant[]>, value) => {
    map[toSnakeCasePreserveTrailingSpace(value)] = getVariantsByOption(
      variants,
      variantGroupName,
      value
    )
    return map
  }, {})
}

const SymbolizeVariantGroupNames = (options: Option[]) => {
  return options.reduce((map: Record<string, string>, { name }) => {
    map[toSnakeCasePreserveTrailingSpace(name)] = name
    return map
  }, {})
}

const isValidFloat = (val: number | null | string) => {
  const parsed = parseFloat(val as string)
  return !isNaN(parsed)
}

const NormalizeProductVariant = (variants: ProductVariant[]) => {
  return variants.map(
    ({
      id,
      archived_at,
      default_variant,
      image,
      inserted_at,
      updated_at,
      limit,
      name,
      options,
      price,
      quantity,
      restrictions,
      sku,
      source_id,
      vat,
      calorie_data
    }: ProductVariant) => ({
      id,
      archived_at,
      default_variant,
      image,
      inserted_at,
      updated_at,
      limit,
      name,
      options,
      price,
      quantity,
      restrictions,
      sku,
      source_id: normalizeSourceId(source_id),
      vat,
      calorie_data
    })
  )
}

const isCaloriesValueValid = (value: string | null) => {
  if (isEmpty(value)) return true //allow empty values
  if (parseFloat(value as string) === 0) return true
  return parseFloat(value as string) > 0
}

const isLimitValueValid = (value: string | null) => {
  if (parseFloat(value as string) === 0) return true
  return parseFloat(value as string) > 0
}

/*
 * this sorts by variant name, then by option's name then the value
 *
 */
const SortVariantsByOptions = (variants: NormalizedVariant) =>
  sortBy(variants, ['name', ['option', ['name', 'value']]])

export {
  isValidFloat,
  isLimitValueValid,
  isCaloriesValueValid,
  SortVariantsByOptions,
  NormalizeProductVariant,
  GroupVariantsByGroupName,
  GroupVariantsByOptionName,
  SymbolizeVariantGroupNames,
  toSnakeCasePreserveTrailingSpace
}
